<template>
    <div class="flex items-center justify-end space-x-4">
        <ui-button-new small icon="fa fa-undo"
                       @click="undo"
                       @mousedown="disableStateTracking"
                       :disabled="undosAvailable()?false:true"
                       v-if="!estimate.readonly"
        >Undo {{ undosAvailable() ? '(' + undosAvailable() + ')' : '' }}
        </ui-button-new>
        <ui-button-new small icon="fa fa-repeat"
                       @click="redo"
                       @mousedown="disableStateTracking"
                       :disabled="redosAvailable()?false:true"
                       v-if="!estimate.readonly"
        >Redo {{ redosAvailable() ? '(' + redosAvailable() + ')' : '' }}
        </ui-button-new>
        <div class="flex">
            <ui-dropdown placement="bottom-end" menu-class="p-3 w-72" :key="userKey">
                <template #default="{ toggle }">
                    <ui-button-new @click="toggle" small primary>
                        Actions
                        <i class="flex-shrink-0 ml-2 fa fa-chevron-down"></i>
                    </ui-button-new>
                </template>

                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-share"
                                      @click="$modals.open('estimate-send', {item: estimate, close:$emit('packageUpdated')})">
                        Send
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-file-excel" @click="downloadXLSX">Download as Excel XLSX</ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-file-pdf" @click="download">Download as PDF</ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-print" @click="print">Print PDF</ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-computer-classic"
                                      @click="copyToClassic">
                        Copy to Classic as Quote
                    </ui-dropdown-item>
                    <ui-dropdown-divider></ui-dropdown-divider>
                    <ui-dropdown-item icon="fas fa-copy"
                                      @click="copyEstimate();close()">
                        Copy Estimate
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-paste"
                                      @click="duplicate();close()">
                        Duplicate Estimate
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-clipboard"
                                      @click="manageClipboard();close()">
                        Manage Library
                    </ui-dropdown-item>
                    <ui-dropdown-divider></ui-dropdown-divider>
                    <ui-dropdown-item icon="fas fa-calculator"
                                      @click="$modals.open('estimate-cost-list', {estimate})">
                        See Cost Breakdown
                    </ui-dropdown-item>
                    <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                    <ui-dropdown-item v-if="!estimate.readonly" danger icon="fa fa-trash" @click="deleteEstimate">Delete
                    </ui-dropdown-item>
                    <ui-dropdown-divider
                        v-if="(user && estimate && !estimate.locked && estimate.editing && (estimate.editingBy !== user.id))"></ui-dropdown-divider>
                    <ui-dropdown-item
                        v-if="(user && estimate && !estimate.locked && estimate.editing && (estimate.editingBy !== user.id))"
                        danger icon="fa fa-lock-open" @click="releaseLock">Force Un-lock of Edit mode
                    </ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>
    </div>
</template>

<script>
import {Estimate} from "../../../models";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import {cloneDeep} from "lodash";
import remoteStorage from "../../../utils/remoteStorage";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import {readonly} from "vue";

export default {
    components: {UiDropdownDivider, UiButtonNew, UiDropdownItem},
    props: {
        estimate: Estimate,
        user: Object,
    },
    data() {
        let estimateClipboardData = [];
        try {
            estimateClipboardData = remoteStorage.getItem('estimate-clipboard') ? JSON.parse(remoteStorage.getItem('estimate-clipboard')) : [];
        } catch (e) {
            //continue on JSON ERROR
        }

        return {
            states: [],
            position: 0,
            selectedPage: [],
            userKey: 1,
            estimateClipboard: estimateClipboardData,
        }
    },
    watch: {
        'estimate.readonly': {
            handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.estimate.clearSnapshots(0);
                    this.estimate.addSnapshot(true);
                    this.states = this.estimate.getSnapshotStore().states;
                    this.position = this.states.length - 1;
                }
            },
        },
        userClipboardToBeWatched: {
            handler(newValue, oldValue) {
                this.estimateClipboard = remoteStorage.getItem('estimate-clipboard') ? JSON.parse(remoteStorage.getItem('estimate-clipboard')) : [];
                this.userKey++;
            },
        },
    },
    computed: {
        userClipboardToBeWatched() {
            return this.user.remoteStorage ? this.user.remoteStorage['estimate-clipboard']
                : '';
        },
    },
    methods: {
        deleteEstimate() {
            this.$modals.confirmDelete({
                title: `Delete estimate #${this.estimate.reference}`,
                button: 'Yes, delete estimate',
                handle: async () => {
                    await this.estimate.delete()
                    this.$emit('deleted', this.estimate.id)
                }
            })
        },
        copyEstimate() {
            this.estimateClipboard[0] = cloneDeep(this.estimate)
            remoteStorage.setItem('estimate-clipboard', JSON.stringify(this.estimateClipboard))
            remoteStorage.setItem('estimate-clipboard-view', 'estimate');
        },
        duplicate() {
            this.$emit('duplicate', this.estimate.id, true)
        },
        undosAvailable() {
            return this.states.length - this.redosAvailable() - 1
        },
        redosAvailable() {
            return this.states.length - this.position - 1
        },
        disableStateTracking() {
            this.estimate.disableSnapshots = true;
            document.activeElement.blur();
        },
        undo() {
            Object.assign(this.estimate, JSON.parse(this.states[--this.position]))
            this.estimate.save();
            this.estimate.disableSnapshots = false;
            this.estimate.buildCollectedItems();
            this.$emit('reflow')
        },
        redo() {
            Object.assign(this.estimate, JSON.parse(this.states[++this.position]))
            this.estimate.save();
            this.estimate.disableSnapshots = false;
            this.estimate.buildCollectedItems();
            this.$emit('reflow')
        },
        print() {
            this.$emit('print', this.estimate.getPrintableUrl())
        },
        download() {
            window.location.href = this.estimate.getDownloadUrl()
        },
        downloadXLSX() {
            window.location.href = this.estimate.getDownloadXLSXUrl()
        },
        copyToClassic() {
            let data;
            this.$modals.confirm({
                title: `Copy this estimate as a new quote in Classic`,
                button: 'Yes, copy to Classic',
                handle: async () => {
                    data = await this.estimate.copyToClassicQuote()
                    setTimeout(() => {
                        this.$modals.confirm({
                            title: `Estimate has been copied to Classic - Navigate to it?`,
                            button: 'Yes - View Quote in Classic',
                            handle: async () => {
                                document.location.href = data._links.classic.view
                            }
                        })
                    }, 200)
                },
            })
        },
        releaseLock() {
            this.$modals.confirm({
                title: `Force Unlock of Edit mode for this estimate?`,
                paragraphs: ['This estimate is currently locked for editing by another user.', 'Force unlocking of edit mode WILL force that user out of edit mode for this estimate and may result in that user loosing any unsaved work.', 'Please attempt to contact the user first and only use this as a last resort.', 'Are you sure you want to proceed?'],
                button: 'Yes, unlock it',
                handle: async () => {
                    await this.estimate.releaseEditLock()
                    close();
                },
            })
        },
        manageClipboard() {
            this.$modals.open('estimate-clipboard', {
                user: this.user
            })
        }
    },
    mounted() {
        eventBus.$on('estimate-history-state-updated', (data) => {
            if (data.estimate === this.estimate) {
                if (this.position + 1 !== data.states.length - 1) {
                    this.estimate.clearSnapshots(this.position + 1, data.states.length - this.position - 2);
                    data = this.estimate.getSnapshotStore();
                    this.states = data.states
                    this.position = data.states.length - 1;
                    this.$forceUpdate();
                } else {
                    this.states = data.states
                    this.position = data.states.length - 1;
                }
            }
        });
        this.estimate.clearSnapshots(0);
        this.estimate.addSnapshot();
        this.states = this.estimate.getSnapshotStore().states;
        this.position = this.states.length - 1;
    }
}
</script>
